import { DateRange } from "@enums/DateRange.enum";
import { IDateFilter } from "@interfaces/filters/IDateFilter";
import { IComboBox } from "@interfaces/IComboBox";
import { IReportParameters } from "@reports/models/IReportParameters";
import { ConstantsService } from "@services/constantsService";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import * as moment from "moment";
import { TimeFilterType } from "@reports/enums/TimeFilterType";
import { IFilterBar, IFilterBarFilter } from "../newResourceFilterBar.component";
import "./dateResourceFilter.scss";

interface IComponentBindings {
    startDate: Date;
    endDate: Date;
    futureDates: boolean;
    isInvalid: boolean;

    reportParameters: IReportParameters;

    restrictToSingleDay: boolean;
    restrictToSingleWeek: boolean;
    includeTimeRange: boolean;
    hideDateRange: boolean;

    maxDateRange: number;
    maxDateRangeUnit: string;

    dateRange: DateRange;

    startHour: number;
    startMinute: number;

    endHour: number;
    endMinute: number;

    timeOnly: boolean;
}

class ComponentController implements angular.IComponentController, IComponentBindings, IFilterBarFilter<IDateFilter>
{
    reportParameters: IReportParameters;

    parent: IFilterBar;

    filterName = "Date";
    filterLabel: string;
    filterTitle: string;
    filterIcon: string = "today";

    show = false;
    isDirty = true;
    showClearFilterButton = false;
    isInvalid = false;

    showNextPrev = true;
    disableNext = false;
    disablePrev = false;

    restrictToSingleDay: boolean = false;
    restrictToSingleWeek: boolean = false;
    includeTimeRange: boolean = true;
    hideDateRange = false;
    timeOnly = false;

    startDate: Date;
    endDate: Date;

    startTime: string;
    endTime: string;

    restrictTimeRange: boolean;
    displayTimeRangeInfo: boolean;

    startHour: number = 0;
    startMinute: number = 0;

    endHour: number = 23;
    endMinute: number = 59;

    dateRange = DateRange.Today;

    previousFilter: IDateFilter;

    onChange: (args: { filter: IDateFilter }) => void;

    dateRangeOptions: IComboBox[] = [];
    dateDayOptions: { label: string, day: string }[] = [];

    displayMaxDateRange: boolean;

    futureDates: boolean;
    minStartDate: Date;
    maxEndDate: Date;

    maxDateRange: number;
    maxDateRangeUnit: string = `M`;
    maxDateRangeUnitName: string;

    isMobile: boolean;

    static $inject = ["$scope", "$element", "veriDate", "constantsService", "translationService"];

    constructor(
        private readonly $scope: angular.IScope,
        public readonly $element: HTMLElement,
        private readonly veriDate: VeriDate,
        private readonly constantsService: ConstantsService,
        private readonly translationService: TranslationService) {
    }

    $onInit() {
        this.isMobile = this.constantsService.getIsMobile();
        this.updateMaxDateRangeUnitName();
        this.addDateRangeOptions();

        if (this.parent) {
            this.parent.registerFilter(this);
        }

        // If we are on mobile reduce the number of options we show
        if (this.isMobile) {
            this.dateRangeOptions.pop();
        }

        this.dateDayOptions = [];
        for (let i = 0; i < 5; i++) {
            const date = moment().add(-i, "d");
            let label = this.veriDate.getDayOfWeek(date.day(), true);

            if (i === 0) { label = this.translationService.tEnum(DateRange, "DateRange", DateRange.Today) }
            if (i === 1) { label = this.translationService.tEnum(DateRange, "DateRange", DateRange.Yesterday) }

            this.dateDayOptions.push({
                label: label,
                day: date.format('Do')
            });
        }

        this.sendUpdate();
    }

    $onDestroy() {
        this.show = false;
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {
        if (this.timeOnly) {
            this.restrictTimeRange = true;
        }

        if (onChangesObj["futureDates"]) {
            this.setMinMaxDates();
        }

        if (onChangesObj["dateRange"] && this.dateRange !== undefined) {
            this.setMinMaxDates();

            this.dateRangeChanged(this.dateRange);
        }

        if (onChangesObj["reportParameters"] && this.reportParameters) {
            this.startDate = this.reportParameters.Start;
            this.endDate = this.reportParameters.End;

            this.setTimeFilterFromReportParameters();

            this.updateFilterLabel();
        }

        // Allow us to trigger an initial update for default timeframe
        if (!this.startDate && !this.endDate) {
            this.createDefaults();
        }
        else {
            this.dateChanged();
        }
    }

    setMinMaxDates() {
        if (this.futureDates) {
            this.maxEndDate = null;
            this.minStartDate = moment().toDate();
        }
        else {
            this.maxEndDate = moment().toDate();
        }
    }

    setTimeFilterFromReportParameters() {
        this.startHour = 0;
        this.startMinute = 0;

        this.endHour = 0;
        this.endMinute = 0;

        if (this.reportParameters.TimeFilterStart) {
            const timeFilterStart = moment.duration(this.reportParameters.TimeFilterStart);
            this.startHour = timeFilterStart.hours();
            this.startMinute = timeFilterStart.minutes();
            this.startTime = this.reportParameters.TimeFilterStart;
            this.restrictTimeRange = true;
        }

        if (this.reportParameters.TimeFilterEnd) {
            const timeFilterEnd = moment.duration(this.reportParameters.TimeFilterEnd);
            this.endHour = timeFilterEnd.hours();
            this.endMinute = timeFilterEnd.minutes();
            this.endTime = this.reportParameters.TimeFilterEnd;
            this.restrictTimeRange = true;
        }

        if (this.startHour + this.startMinute + this.endHour + this.endMinute == 0) {
            this.startTimeChanged(0, 0);
            this.endTimeChanged(23, 59);

            if (this.timeOnly) {
                this.isDirty = false;
            }
        }
    }

    clearFilter() {

    }

    toggleFilter(show: boolean) {
        this.show = show;
    }

    startDateChanged() {
        this.dateChanged();
    }

    endDateChanged() {
        this.dateChanged();
    }

    dateRangeChanged(dateRange: DateRange) {
        this.dateRange = dateRange;
        const dates = this.veriDate.dateRangeFromEnum(this.dateRange);

        this.startDate = dates.start;
        this.endDate = dates.end;

        this.isInvalid = false;
        this.displayMaxDateRange = false;

        this.checkTimeRanges();

        this.updateFilterLabel();
    }

    createFilterObject(): IDateFilter {
        return {
            start: this.startDate,
            end: this.endDate,
            startTime: this.restrictTimeRange ? this.startTime : null,
            endTime: this.restrictTimeRange ? this.endTime : null,
            filterType: this.restrictTimeRange ? TimeFilterType.Daily : null
    };
    }

    startTimeChanged(hour: number, minute: number) {
        this.startHour = hour || 0;
        this.startMinute = minute || 0;

        this.startTime = this.toTimespan(this.startHour, this.startMinute, 0);
        this.checkTimeRanges();
        this.updateFilterLabel();
    }

    endTimeChanged(hour: number, minute: number) {
        this.endHour = hour || 0;
        this.endMinute = minute || 0;

        this.endTime = this.toTimespan(this.endHour, this.endMinute, 59);
        this.checkTimeRanges();

        this.updateFilterLabel();
    }

    restrictTimeRangeChanged() {

        this.startTimeChanged(this.startHour, this.startMinute);
        this.endTimeChanged(this.endHour, this.endMinute);
    }

    nextClicked() {
        var currentRange = this.getDateRangeInDays();

        this.startDate = moment(this.startDate).add(currentRange, "d").toDate();
        this.endDate = moment(this.endDate).add(currentRange, "d").toDate();

        this.dateChanged();
        this.sendUpdate();

        this.$scope.$applyAsync();
    }

    prevClicked() {
        var currentRange = this.getDateRangeInDays();

        this.startDate = moment(this.startDate).add(-currentRange, "d").toDate();
        this.endDate = moment(this.endDate).add(-currentRange, "d").toDate();

        this.dateChanged();
        this.sendUpdate();

        this.$scope.$applyAsync();
    }

    // ------------------

    private updateMaxDateRangeUnitName(): void {
        const unit = this.maxDateRangeUnit;

        if (unit === "year" || unit === "years" || unit === "y") {
            this.maxDateRangeUnitName = `year`;

        }
        else if (unit === "month" || unit === "months" || unit === "M") {
            this.maxDateRangeUnitName = `month`;

        }
        else if (unit === "week" || unit === "weeks" || unit === "w") {
            this.maxDateRangeUnitName = `week`;

        }
        else if (unit === "day" || unit === "days" || unit === "d") {
            this.maxDateRangeUnitName = `day`;
        }
    }

    private addDateRangeOptions(): void {

        this.dateRangeOptions.push({ Name: this.translationService.tEnum(DateRange, "DateRange", DateRange.Today), Value: DateRange.Today });
        this.dateRangeOptions.push({ Name: this.translationService.tEnum(DateRange, "DateRange", DateRange.Yesterday), Value: DateRange.Yesterday });

        if (this.restrictToSingleDay) {
            return;
        }

        this.dateRangeOptions.push({ Name: this.translationService.tEnum(DateRange, "DateRange", DateRange.Last7Days), Value: DateRange.Last7Days });

        if (this.restrictToSingleWeek) {
            return;
        }

        this.dateRangeOptions.push({ Name: this.translationService.tEnum(DateRange, "DateRange", DateRange.Last30Days), Value: DateRange.Last30Days });
        this.dateRangeOptions.push({ Name: this.translationService.tEnum(DateRange, "DateRange", DateRange.LastMonth), Value: DateRange.LastMonth });
    }

    private toTimespan(hour: number, minute: number, seconds: number): string {
        return moment().hour(hour).minutes(minute).seconds(seconds).format("HH:mm:ss");
    }

    private fromTimespan(time: string): moment.Moment {
        return moment.utc(time, "HH:mm:ss").local();
    }

    private createDefaults() {

        this.dateRangeChanged(DateRange.Today);
        this.sendUpdate();
    }

    private checkTimeRanges() {
        this.displayTimeRangeInfo = false;

        if (this.startTime > this.endTime && this.startDate.toLocaleDateString() == this.endDate.toLocaleDateString()) {
            this.displayTimeRangeInfo = true;
        }
    }

    private sendUpdate() {
        const filter = this.createFilterObject();

        if (this.onChange) {
            this.onChange({ filter: filter });
        }
    }

    private dateChanged() {
        this.endDate = (this.restrictToSingleDay) ? this.startDate : this.endDate;
        this.dateRange = this.veriDate.dateRangeIntoEnum({ start: this.startDate, end: this.endDate });

        if (moment(this.startDate).isAfter(moment(this.endDate))) {
            this.isInvalid = true;
            this.updateFilterLabel();
            return;
        }

        this.maxDateRange = this.maxDateRange || 1;

        const duration: moment.unitOfTime.DurationConstructor = `${this.maxDateRangeUnit}` as any;
        this.displayMaxDateRange = moment(this.startDate).isBefore(moment(this.endDate).add(-this.maxDateRange, duration), "day");

        if (this.displayMaxDateRange || !this.startDate || !this.endDate) {
            this.isInvalid = true;
        }
        else {
            this.isInvalid = false;
        }

        this.checkTimeRanges();

        this.updateFilterLabel();

        if (!this.futureDates) {
            this.disableNext = moment(this.endDate).isSameOrAfter(moment(), "d");

        } else {
            this.disablePrev = moment(this.startDate).isSameOrBefore(moment(), "d");
        }
    }

    private updateFilterLabel() {

        if (!this.hideDateRange) {
            this.filterLabel = this.veriDate.getReadableDateRangeLabel(this.startDate, this.endDate, this.dateRange);
        }

        if (this.timeOnly) {
            this.filterLabel = "";
        }

        if (this.restrictTimeRange && this.startTime && this.endTime) {
            if (this.filterLabel != "") {
                this.filterLabel += ". ";
            }

            this.filterLabel += `${this.startTime.slice(0, -3)} to ${this.endTime.slice(0, -3)}`;
        }

        if (this.filterLabel == "") {
            this.filterLabel = this.translationService.t('filters.dateResourceFilter.anyTime');
        }

        if (this.isInvalid) {
            this.filterLabel = this.translationService.t('filters.dateResourceFilter.invalidDateRange');
        }
    }

    private getDateRangeInDays(): number {
        var currentRange = (this.endDate.valueOf() - this.startDate.valueOf()) / 1000 / 60 / 60 / 24;

        return currentRange == 0
            ? 1 // If we are the same date, then assume its one day (e.g. Today or Yesterday)
            : Math.ceil(currentRange);
    }
}

export class DateResourceFilterComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    require: { [index: string]: string; };
    template: string;

    constructor() {
        this.bindings = {
            dateRange: "<",
            futureDates: "<?",

            reportParameters: "<?",

            startDate: "<?",
            endDate: "<?",

            startHour: "<?",
            startMinute: "<?",

            endHour: "<?",
            endMinute: "<?",

            restrictToSingleDay: "<?",
            restrictToSingleWeek: "<?",

            maxDateRange: "<?",
            maxDateRangeUnit: "<?",

            includeTimeRange: "<",
            hideDateRange: "<",
            timeOnly: "<",

            onChange: "&"
        };

        this.require = {
            parent: "^?newResourceFilterBar"
        };

        this.controller = ComponentController;
        this.template = require("./dateResourceFilter.html");
    }
}
